// Guía de Ayuda Oficial de BotMix — Dashboard
// Soporte Internacional en 5 Idiomas (es, en, pt, fr, ar)

const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Centro de Gestión y Monitoreo en Vivo — Dashboard",
    summary:
      "El Dashboard es el centro de control neurálgico de tu empresa en BotMix. Desde aquí puedes supervisar en tiempo real el flujo de conversaciones, la productividad de tus asesores, la satisfacción de tus clientes (NPS) y el consumo de los recursos de tu plan mensual. Toda la información se actualiza de manera automática mediante WebSockets sin necesidad de recargar la página.",
  },
  sections: [
    {
      id: "filtros",
      title: "1. Filtros de Visualización y Rango Temporal",
      description:
        "Herramientas para segmentar cronológicamente las métricas y reportes del sistema.",
      items: [
        {
          id: "toggle_filters",
          title: "Mostrar / Ocultar Filtros",
          definition:
            "Al pulsar el botón superior derecho de la pantalla, se despliega o repliega la barra de selección de fechas. Esta acción te permite enfocar la pantalla en el periodo de análisis que necesitas sin saturar el espacio visual de trabajo.",
          importance:
            "Ideal para aislar periodos específicos de auditoría como cierres semanales, quincenales o mensuales sin interferir con las métricas acumuladas.",
        },
        {
          id: "date_range",
          title: "Rango de Fechas (Fecha Inicial y Fecha Final)",
          definition:
            "Permite acotar con precisión de calendario los límites cronológicos del informe. Puedes seleccionar cualquier fecha de inicio y corte para comparar campañas, evaluar turnos o auditar jornadas específicas.",
          importance:
            "Si no defines fechas manuales, el sistema aplica automáticamente un rango predeterminado de los últimos 30 días para ofrecer una visión general equilibrada.",
        },
        {
          id: "btn_filter",
          title: "Ejecución del Filtro (Botón Filtrar)",
          definition:
            "Procesa los parámetros de fecha configurados y recalcula inmediatamente todos los contadores de estado, barras de NPS, tablas de rendimiento de asesores y gráficas de tendencias sin refrescar la página.",
        },
      ],
    },
    {
      id: "indicadores",
      title: "2. Indicadores Generales de Conversación (KPIs en Vivo)",
      description:
        "Métricas cuantitativas que reflejan el estado actual de las comunicaciones en tiempo real.",
      items: [
        {
          id: "atencion_actual",
          title: "Atención Actual",
          definition:
            "Cuantifica el número total de clientes que en este instante exacto están manteniendo una conversación activa con un asesor humano en la plataforma.",
          importance:
            "Representa el nivel de carga concurrente que está soportando tu equipo. Permite al supervisor verificar si la capacidad instalada del personal en línea es suficiente para absorber la demanda actual.",
        },
        {
          id: "esperando",
          title: "Esperando (Cola de Espera)",
          definition:
            "Total de clientes que ya escribieron o fueron transferidos por un chatbot/flujo y se encuentran en la bandeja general aguardando a que un asesor tome el ticket.",
          alert:
            "Un incremento prolongado en este indicador evidencia cuellos de botella en la atención, falta de personal disponible o demoras excesivas en la primera respuesta.",
        },
        {
          id: "finalizados",
          title: "Finalizados",
          definition:
            "Total acumulado de tickets que fueron atendidos, resueltos y formalmente cerrados por los asesores durante la ventana de tiempo consultada. Refleja la capacidad resolutiva total de la operación.",
        },
        {
          id: "grupos",
          title: "Grupos",
          definition:
            "Número de chats grupales de WhatsApp en los que la empresa participa o mantiene vinculación activa mediante la plataforma para soporte comunitario o difusión comercial.",
        },
        {
          id: "agentes_activos",
          title: "Agentes Activos",
          definition:
            "Proporción directa entre el personal actualmente conectado frente al total de usuarios registrados en el sistema (ejemplo: 4 / 10). Permite auditar en tiempo real la puntualidad y presencia del equipo de atención.",
          importance:
            "Te ayuda a corroborar si los asesores que tienen turno asignado realmente tienen su sesión iniciada y operativa en el sistema.",
        },
        {
          id: "contactos_plan",
          title: "Contactos del Plan",
          definition:
            "Registro de contactos únicos almacenados en la base de datos de tu cuenta frente al cupo máximo estipulado en tu plan comercial contratado.",
        },
        {
          id: "mensajes_recibidos",
          title: "Mensajes Recibidos",
          definition:
            "Volumen global de mensajes entrantes enviados por tus clientes a todas tus líneas conectadas (incluye texto plano, notas de audio, imágenes, documentos PDF y ubicaciones geográficas).",
        },
        {
          id: "mensajes_enviados",
          title: "Mensajes Enviados",
          definition:
            "Total de mensajes emitidos desde la plataforma hacia los clientes, integrando tanto las respuestas manuales de tus agentes como los envíos automáticos de bots y campañas masivas.",
        },
        {
          id: "tiempo_atencion",
          title: "T.P. de Atención (Tiempo Promedio de Atención)",
          definition:
            "Duración promedio cronometrada en horas, minutos y segundos (00h 00m 00s) que un agente o asesor dedica a la resolución activa de una conversación, desde que acepta el ticket hasta su cierre formal.",
          importance:
            "Permite identificar la complejidad de las consultas recibidas y evaluar la agilidad operativa de los operadores.",
        },
        {
          id: "tiempo_espera",
          title: "T.P. de Espera (Tiempo Promedio de Espera)",
          definition:
            "Lapso promedio en horas, minutos y segundos (00h 00m 00s) que transcurre desde que un cliente entra en la cola de atención o finaliza su interacción con el chatbot hasta que un asesor humano toma el ticket.",
          alert:
            "Tiempos de espera prolongados generan insatisfacción y abandono. Se recomienda optimizar las colas y los flujos automatizados para mantener este indicador en los valores más bajos posibles.",
        },
        {
          id: "iniciados_agente",
          title: "Iniciados por Agente",
          definition:
            "Contabiliza los tickets o conversaciones creados de forma proactiva por los miembros del equipo (demanda activa / outbound) mediante el botón de nuevo ticket para contactar directamente a un cliente.",
          importance:
            "Mide el esfuerzo de prospección comercial, seguimiento postventa, gestiones de cobranza o contacto preventivo del equipo.",
        },
        {
          id: "iniciados_cliente",
          title: "Iniciados por Cliente",
          definition:
            "Total de tickets generados automáticamente cuando un cliente o contacto externo escribe hacia las líneas de la empresa desde WhatsApp, Instagram o Telegram (demanda receptiva / inbound).",
          importance:
            "Refleja el flujo natural de demanda orgánica y consultas que ingresan hacia los canales de atención de tu empresa.",
        },
      ],
    },
    {
      id: "consumo_plan",
      title: "3. Consumo de tu Plan (Límites y Cupos Mensuales)",
      description:
        "Supervisión detallada de la cuota utilizada en cada una de las funcionalidades de tu suscripción.",
      items: [
        {
          id: "plan_contactos",
          title: "Contactos Almacenados",
          definition:
            "Cantidad de registros de clientes almacenados en la libreta de direcciones frente al límite contratado en tu plan.",
        },
        {
          id: "plan_marcas",
          title: "Marcas Comerciales",
          definition:
            "Número de identidades o marcas comerciales administradas de forma independiente dentro de tu cuenta multi-empresa.",
        },
        {
          id: "plan_embudos",
          title: "Embudos de Venta (Funnels)",
          definition:
            "Tableros CRM tipo Kanban estructurados para el seguimiento de etapas de conversión y procesos de venta activos.",
        },
        {
          id: "plan_wp_qr",
          title: "Conexiones WhatsApp QR",
          definition:
            "Dispositivos o líneas vinculadas a través del escaneo de código QR (motor Baileys) para canales tradicionales de mensajería.",
        },
        {
          id: "plan_wp_api",
          title: "Conexiones WhatsApp API Oficial",
          definition:
            "Líneas empresariales conectadas mediante Meta Cloud API Oficial, diseñadas para alto volumen y cero riesgo de suspensión.",
        },
        {
          id: "plan_redes",
          title: "Canales Sociales (Instagram / Messenger / Telegram)",
          definition:
            "Cuentas omnicanal vinculadas a la plataforma para centralizar todas las conversaciones de redes sociales en una sola bandeja.",
        },
        {
          id: "plan_correos",
          title: "Cuentas de Correo Electrónico",
          definition:
            "Casillas de email corporativo conectadas para la recepción y respuesta de requerimientos de soporte mediante correo tradicional.",
        },
        {
          id: "plan_flujos",
          title: "Flujos Automatizados (Flows)",
          definition:
            "Cantidad de chatbots interactivos y árboles de decisión construidos activamente en la herramienta visual FlowBuilder.",
        },
        {
          id: "plan_publicaciones",
          title: "Publicaciones Masivas (Estados, Grupos y Comunidades)",
          definition:
            "Contador mensual de difusiones automáticas programadas en canales de difusión y estados de mensajería.",
        },
        {
          id: "plan_widgets",
          title: "Widgets Web",
          definition:
            "Chatbots flotantes embebidos en páginas externas o landing pages para captura y atención inmediata de visitantes web.",
        },
        {
          id: "plan_mensajes_mes",
          title: "Mensajes del Mes",
          definition:
            "Balance de mensajes mensuales procesados frente al límite contratado. Cuando un indicador expone la leyenda 'Ilimitado', significa que tu suscripción no posee tope restrictivo.",
          importance:
            "Si llegas al 100% de la cuota contratada, el sistema activará la alerta visual de límites en la barra superior.",
        },
      ],
    },
    {
      id: "nps",
      title: "4. Encuesta de Satisfacción (NPS) y Control de Calidad",
      description:
        "Métricas de percepción y lealtad calculadas mediante el estándar internacional Net Promoter Score.",
      items: [
        {
          id: "nps_score",
          title: "Puntaje NPS Global (Net Promoter Score)",
          definition:
            "Se obtiene restando el porcentaje de clientes Detractores al porcentaje de clientes Promotores. El resultado oscila en una escala entre -100 y +100 puntos. Refleja fielmente la salud de la experiencia del cliente.",
          importance:
            "De +50 a +100 representa calidad de clase mundial; de 0 a +49 es un servicio bueno con áreas de mejora; valores negativos alertan sobre problemas críticos en la atención.",
        },
        {
          id: "nps_promotores",
          title: "Clientes Promotores (Verde)",
          definition:
            "Clientes que calificaron el servicio con la nota máxima recomendando activamente tu marca y valorando la rapidez y amabilidad del asesor.",
        },
        {
          id: "nps_neutrales",
          title: "Clientes Neutrales (Amarillo)",
          definition:
            "Clientes con calificación intermedia; recibieron la atención necesaria pero sin entusiasmo particular, siendo susceptibles a ofertas competidoras.",
        },
        {
          id: "nps_detractores",
          title: "Clientes Detractores (Rojo)",
          definition:
            "Clientes insatisfechos con el tiempo de espera, el trato o la falta de solución a su problema. Requieren auditoría inmediata de los registros de chat.",
        },
      ],
    },
    {
      id: "atendimientos",
      title: "5. Información de Atendimientos y Evaluaciones",
      description:
        "Resumen global del estado de cierre y nivel de respuesta de las encuestas aplicadas.",
      items: [
        {
          id: "total_tickets",
          title: "Total de Tickets",
          definition:
            "Universo general de casos de soporte y ventas gestionados en el intervalo temporal seleccionado en los filtros.",
        },
        {
          id: "esperando_evaluacion",
          title: "Tickets Esperando Evaluación",
          definition:
            "Conversaciones finalizadas donde el disparador automático remitió la encuesta de calidad al cliente pero este aún no ha enviado su respuesta.",
        },
        {
          id: "sin_evaluacion",
          title: "Tickets sin Evaluación",
          definition:
            "Casos cerrados sin calificación por vencimiento del tiempo de respuesta o por clientes que omitieron responder a la encuesta.",
        },
        {
          id: "evaluados",
          title: "Tickets Evaluados",
          definition:
            "Total de encuestas contestadas satisfactoriamente por los usuarios durante el periodo consultado.",
        },
        {
          id: "indice_evaluacion",
          title: "Índice de Evaluación",
          definition:
            "Medición porcentual que evalúa la tasa de conversión y participación de los clientes en las encuestas de calidad sobre el total de tickets cerrados.",
        },
      ],
    },
    {
      id: "tabla_agentes",
      title: "6. Tabla de Rendimiento por Asesor (Monitoreo Individual)",
      description:
        "Supervisión del desempeño, velocidad de respuesta y calidad de cada integrante del equipo.",
      items: [
        {
          id: "col_nombre",
          title: "Identificación del Asesor",
          definition:
            "Nombre completo y cuenta del asesor responsable de la atención en la plataforma.",
        },
        {
          id: "col_score",
          title: "Puntaje Promedio (Score)",
          definition:
            "Calificación ponderada obtenida en función de las notas recibidas de los clientes atendidos por este asesor en particular.",
        },
        {
          id: "col_tickets",
          title: "Tickets Atendidos y Evaluados",
          definition:
            "Relación cuantitativa entre las solicitudes resueltas por el asesor y las calificaciones efectivas que recibió de parte de los clientes.",
        },
        {
          id: "col_tpe",
          title: "Tiempo Promedio de Espera (TPE)",
          definition:
            "Duración promedio en minutos que tardó el asesor en enviar el primer mensaje de respuesta desde que el ticket entró en su bandeja de atención.",
          importance:
            "Recomendación operativa: Mantener el TPE por debajo de 2 minutos para evitar la pérdida de prospectos calificados.",
        },
        {
          id: "col_tpa",
          title: "Tiempo Promedio de Atención (TPA)",
          definition:
            "Minutos transcurridos en promedio desde la primera interacción del agente hasta el cierre formal y resolución del ticket.",
        },
        {
          id: "col_estado",
          title: "Estado de Conexión en Vivo",
          definition:
            "Indicador visual circular verde para agentes con sesión activa en navegador y gris para agentes desconectados o inactivos.",
        },
        {
          id: "exportar_excel",
          title: "Exportación a Formato Excel",
          definition:
            "Función que permite descargar inmediatamente la hoja de cálculo 'relatorio-de-atendentes.xlsx' para auditorías de supervisión y liquidación de comisiones.",
        },
      ],
    },
    {
      id: "graficos",
      title: "7. Gráficos de Tendencias y Distribución Horaria",
      description:
        "Visualizaciones analíticas para balancear recursos y detectar patrones de demanda.",
      items: [
        {
          id: "grafico_usuarios",
          title: "Distribución de Atención por Usuario",
          definition:
            "Gráfico comparativo que visualiza la distribución de tickets resueltos por cada asesor para detectar desequilibrios en la asignación de trabajo.",
        },
        {
          id: "grafico_fechas",
          title: "Flujo Temporal por Día y Franja Horaria",
          definition:
            "Histograma cronológico que detecta los días y horas pico de mayor afluencia de mensajes entrantes.",
          importance:
            "Uso estratégico: Facilita la asignación eficiente de turnos de trabajo y descansos en las franjas de máxima demanda.",
        },
      ],
    },
    {
      id: "faq",
      title: "8. Preguntas Frecuentes y Buenas Prácticas (FAQ)",
      description:
        "Respuestas a las dudas operativas más comunes sobre el comportamiento del Dashboard.",
      items: [
        {
          id: "faq_refresh",
          title: "¿Es necesario refrescar manualmente la pantalla para ver actualizaciones?",
          definition:
            "No. El Dashboard está conectado en tiempo real mediante WebSockets a los eventos del servidor, recalculando cifras al instante ante cada nuevo mensaje o ticket, además de un ciclo de refresco suave cada 60 segundos.",
        },
        {
          id: "faq_offline",
          title: "¿Por qué un agente figura como Offline si afirma estar trabajando?",
          definition:
            "El sistema monitoriza la actividad de la pestaña del navegador. Si el agente minimizó la ventana, suspendió el equipo o perdió conexión a internet, figurará desconectado hasta que reanude actividad.",
        },
        {
          id: "faq_tiempos",
          title: "¿Cómo se optimizan los tiempos de espera elevados?",
          definition:
            "Mediante la configuración de flujos en FlowBuilder que respondan de inmediato, califiquen al cliente y lo enruten con datos previos al asesor correspondiente.",
        },
      ],
    },
  ],
};

const en = {
  "header": {
    "badge": "BotMix Official Guide",
    "title": "Live Management and Monitoring Center — Dashboard",
    "summary": "The Dashboard is the operational control center of your company in BotMix. From here you can supervise real-time conversation flows, agent productivity, customer satisfaction (NPS), and monthly plan resource consumption. All metrics update automatically via WebSockets without page reload."
  },
  "sections": [
    {
      "id": "filtros",
      "title": "1. Display Filters and Time Range",
      "description": "Tools to segment system metrics and reports chronologically.",
      "items": [
        {
          "id": "toggle_filters",
          "title": "Show / Hide Filters",
          "definition": "Clicking the top right button expands or collapses the date selection bar, allowing you to focus on the desired period without workspace clutter.",
          "importance": "Ideal for auditing specific periods like weekly, bi-weekly, or monthly closings."
        },
        {
          "id": "date_range",
          "title": "Date Range (Start Date and End Date)",
          "definition": "Allows setting calendar boundaries for the report to evaluate shifts, audit days, or compare campaigns.",
          "importance": "If no manual dates are set, the system automatically defaults to the last 30 days."
        },
        {
          "id": "btn_filter",
          "title": "Filter Execution (Filter Button)",
          "definition": "Applies configured date parameters and recalculates state counters, NPS, agent tables, and charts instantly."
        }
      ]
    },
    {
      "id": "indicadores",
      "title": "2. General Conversation Indicators (Live KPIs)",
      "description": "Quantitative metrics reflecting the real-time status of communications.",
      "items": [
        {
          "id": "atencion_actual",
          "title": "Current Attendance",
          "definition": "Total number of customers currently having an active conversation with a human agent on the platform.",
          "importance": "Represents current workload capacity to evaluate if staffing is sufficient."
        },
        {
          "id": "esperando",
          "title": "Waiting (Waiting Queue)",
          "definition": "Customers waiting in the general queue for an agent to take their ticket after interacting with a chatbot/flow.",
          "alert": "Prolonged increases indicate bottlenecks, staffing shortages, or delays in first response."
        },
        {
          "id": "finalizados",
          "title": "Finalized",
          "definition": "Total closed and resolved tickets within the analyzed period."
        },
        {
          "id": "grupos",
          "title": "Groups",
          "definition": "Active WhatsApp group chats connected to the company."
        },
        {
          "id": "agentes_activos",
          "title": "Active Agents",
          "definition": "Online agents versus total registered agents in the company."
        },
        {
          "id": "contactos_plan",
          "title": "Plan Contacts",
          "definition": "Ratio of stored contacts versus maximum contacts allowed by plan."
        },
        {
          "id": "mensajes_recibidos",
          "title": "Received Messages",
          "definition": "Total incoming messages processed across all connected channels."
        },
        {
          "id": "mensajes_enviados",
          "title": "Sent Messages",
          "definition": "Total outbound messages dispatched by agents, automations, and chatbots."
        },
        {
          "id": "tiempo_atencion",
          "title": "Avg. Service Time (AST)",
          "definition": "Average duration from ticket acceptance until resolution.",
          "importance": "Measures resolution efficiency per conversation."
        },
        {
          "id": "tiempo_espera",
          "title": "Avg. Wait Time (AWT)",
          "definition": "Average time a customer waits from entering queue until agent takes the ticket.",
          "alert": "High wait times directly harm user experience and satisfaction."
        },
        {
          "id": "iniciados_agente",
          "title": "Initiated by Agent",
          "definition": "Outbound tickets created manually by agents using the '+' button.",
          "importance": "Reflects proactive outreach and follow-up efforts."
        },
        {
          "id": "iniciados_cliente",
          "title": "Initiated by Customer",
          "definition": "Inbound tickets generated by customer-initiated messages."
        }
      ]
    },
    {
      "id": "consumo_plan",
      "title": "3. Plan Consumption (Monthly Limits and Quotas)",
      "description": "Resource consumption tracking against your active subscription plan.",
      "items": [
        {
          "id": "plan_contactos",
          "title": "Stored Contacts",
          "definition": "Total contacts registered in CRM against plan limit."
        },
        {
          "id": "plan_marcas",
          "title": "Commercial Brands",
          "definition": "Multi-brand workspaces enabled in the account."
        },
        {
          "id": "plan_embudos",
          "title": "Sales Funnels (CRM)",
          "definition": "Active Kanban pipelines and funnels."
        },
        {
          "id": "plan_conexiones_qr",
          "title": "WhatsApp Baileys Connections",
          "definition": "Connected WhatsApp numbers via QR code."
        },
        {
          "id": "plan_conexiones_oficial",
          "title": "Official Meta API WhatsApp Connections",
          "definition": "Verified WhatsApp Business API cloud channels."
        },
        {
          "id": "plan_redes_sociales",
          "title": "Social Channels (Instagram / Messenger / Telegram)",
          "definition": "Connected social messaging channels."
        },
        {
          "id": "plan_correos",
          "title": "Email Accounts",
          "definition": "Inboxes synchronized for omnichannel ticketing."
        },
        {
          "id": "plan_flujos",
          "title": "Automated Chatbot Flows",
          "definition": "Active Flowbuilder bots published in the system."
        },
        {
          "id": "plan_publicaciones",
          "title": "Mass Broadcast Posts (Status, Groups & Communities)",
          "definition": "Monthly broadcasts sent to WhatsApp status and groups."
        },
        {
          "id": "plan_widgets",
          "title": "Web Widgets",
          "definition": "Live chat widgets embedded in external websites."
        },
        {
          "id": "plan_mensajes_mes",
          "title": "Messages of the Month",
          "definition": "Monthly throughput quota consumption."
        }
      ]
    },
    {
      "id": "nps",
      "title": "4. Customer Satisfaction Survey (NPS) and Quality Control",
      "description": "Net Promoter Score satisfaction metrics evaluated by end users.",
      "items": [
        {
          "id": "nps_score",
          "title": "Global NPS Score",
          "definition": "Calculated score: % Promoters (9-10) minus % Detractors (1-6)."
        },
        {
          "id": "nps_promotores",
          "title": "Promoters (Green, 9-10)",
          "definition": "Satisfied loyal customers who recommend your service."
        },
        {
          "id": "nps_neutrales",
          "title": "Neutral (Yellow, 7-8)",
          "definition": "Satisfied but unenthusiastic customers."
        },
        {
          "id": "nps_detractores",
          "title": "Detractors (Red, 1-6)",
          "definition": "Unhappy customers with negative experiences."
        }
      ]
    },
    {
      "id": "evaluaciones",
      "title": "5. Service Information and Rating Breakdown",
      "description": "Total tickets processed, rated, and unrated.",
      "items": [
        {
          "id": "total_tickets",
          "title": "Total Tickets",
          "definition": "Overall volume of tickets processed."
        },
        {
          "id": "tickets_espera_eval",
          "title": "Tickets Awaiting Rating",
          "definition": "Resolved tickets where survey was sent but not answered yet."
        },
        {
          "id": "tickets_sin_eval",
          "title": "Tickets Without Rating",
          "definition": "Tickets closed without customer evaluation."
        },
        {
          "id": "tickets_evaluados",
          "title": "Rated Tickets",
          "definition": "Tickets with completed surveys."
        },
        {
          "id": "indice_eval",
          "title": "Rating Index",
          "definition": "Percentage of closed tickets that received a response."
        }
      ]
    },
    {
      "id": "rendimiento_asesores",
      "title": "6. Agent Performance Table (Individual Monitoring)",
      "description": "KPI breakdown per agent: score, resolution time, wait time, and live status.",
      "items": [
        {
          "id": "asesor_nombre",
          "title": "Agent Identification",
          "definition": "Name and avatar of the team member."
        },
        {
          "id": "asesor_score",
          "title": "Average Score",
          "definition": "Mean NPS rating received by the agent."
        },
        {
          "id": "asesor_atendidos",
          "title": "Attended and Rated Tickets",
          "definition": "Volume of attended and evaluated conversations."
        },
        {
          "id": "asesor_tpe",
          "title": "Average Wait Time (AWT)",
          "definition": "Average time customers waited before this agent answered."
        },
        {
          "id": "asesor_tpa",
          "title": "Average Service Time (AST)",
          "definition": "Average time spent solving tickets."
        },
        {
          "id": "asesor_estado",
          "title": "Live Connection Status",
          "definition": "Real-time online/offline indicator."
        },
        {
          "id": "asesor_export",
          "title": "Excel Export",
          "definition": "Downloads complete performance data to spreadsheet."
        }
      ]
    },
    {
      "id": "graficos",
      "title": "7. Trend Charts and Hourly Distribution",
      "description": "Visual trends by agent and peak conversation hours.",
      "items": [
        {
          "id": "grafico_usuario",
          "title": "Attendance Distribution by User",
          "definition": "Comparative chart of workload across agents."
        },
        {
          "id": "grafico_tiempo",
          "title": "Hourly and Daily Flow",
          "definition": "Temporal volume curves to identify peak operational hours."
        }
      ]
    },
    {
      "id": "faq",
      "title": "8. Frequently Asked Questions & Best Practices (FAQ)",
      "description": "Guidelines for optimal dashboard usage and team productivity.",
      "items": [
        {
          "id": "faq_refresh",
          "title": "Do I need to manually refresh the screen?",
          "definition": "No, data streams in real time via WebSockets."
        },
        {
          "id": "faq_offline",
          "title": "Why does an agent show as Offline while working?",
          "definition": "Occurs if the agent closed their tab or lost browser socket connection."
        },
        {
          "id": "faq_optimizar",
          "title": "How to optimize high wait times?",
          "definition": "Balance queue assignments, activate automated greetings, and distribute workload evenly."
        }
      ]
    }
  ]
};

const pt = {
  "header": {
    "badge": "Guia Oficial da BotMix",
    "title": "Centro de Gestão e Monitoramento em Tempo Real — Dashboard",
    "summary": "O Dashboard é o centro de controle da sua empresa na BotMix. Acompanhe em tempo real o fluxo de conversas, a produtividade dos atendentes, a satisfação dos clientes (NPS) e o consumo de recursos do plano mensal. Atualização automática via WebSockets."
  },
  "sections": [
    {
      "id": "filtros",
      "title": "1. Filtros de Visualização e Período",
      "description": "Ferramentas para segmentação cronológica de métricas e relatórios.",
      "items": [
        {
          "id": "toggle_filters",
          "title": "Mostrar / Ocultar Filtros",
          "definition": "Expande ou recolhe a barra de seleção de datas para visualização limpa."
        },
        {
          "id": "date_range",
          "title": "Intervalo de Datas",
          "definition": "Permite definir com exatidão os limites temporais da análise."
        },
        {
          "id": "btn_filter",
          "title": "Botão Filtrar",
          "definition": "Recalcula instantaneamente os contadores e gráficos com base nas datas selecionadas."
        }
      ]
    },
    {
      "id": "indicadores",
      "title": "2. Indicadores Gerais de Atendimento (KPIs em Tempo Real)",
      "description": "Métricas que refletem o estado atual das comunicações da empresa.",
      "items": [
        {
          "id": "atencion_actual",
          "title": "Atendimentos Atuais",
          "definition": "Clientes conversando ativamente com atendentes humanos neste instante."
        },
        {
          "id": "esperando",
          "title": "Aguardando (Fila)",
          "definition": "Clientes aguardando na fila para que um atendente assuma o ticket."
        },
        {
          "id": "finalizados",
          "title": "Finalizados",
          "definition": "Total de tickets resolvidos e concluídos no período."
        },
        {
          "id": "grupos",
          "title": "Grupos",
          "definition": "Grupos de WhatsApp ativos conectados ao sistema."
        },
        {
          "id": "agentes_activos",
          "title": "Atendentes Ativos",
          "definition": "Atendentes online versus total de usuários cadastrados."
        },
        {
          "id": "contactos_plan",
          "title": "Contatos do Plano",
          "definition": "Contatos salvos em relação ao limite contratado."
        },
        {
          "id": "mensajes_recibidos",
          "title": "Mensagens Recebidas",
          "definition": "Volume total de mensagens recebidas em todos os canais."
        },
        {
          "id": "mensajes_enviados",
          "title": "Mensagens Enviadas",
          "definition": "Total de mensagens enviadas por atendentes e robôs."
        },
        {
          "id": "tiempo_atencion",
          "title": "T.M. de Atendimento (TMA)",
          "definition": "Tempo médio gasto desde a abertura até a resolução do ticket."
        },
        {
          "id": "tiempo_espera",
          "title": "T.M. de Espera (TME)",
          "definition": "Tempo médio que o cliente aguardou na fila antes do primeiro atendimento."
        },
        {
          "id": "iniciados_agente",
          "title": "Iniciados por Atendente",
          "definition": "Tickets ativos criados proativamente por atendentes pelo botão '+'."
        },
        {
          "id": "iniciados_cliente",
          "title": "Iniciados por Cliente",
          "definition": "Tickets receptivos originados por mensagens de clientes."
        }
      ]
    },
    {
      "id": "consumo_plan",
      "title": "3. Consumo do Plano (Limites Mensais)",
      "description": "Acompanhamento do uso de recursos do plano contratado.",
      "items": [
        {
          "id": "plan_contactos",
          "title": "Contatos Armazenados",
          "definition": "Total de contatos cadastrados no CRM."
        },
        {
          "id": "plan_marcas",
          "title": "Marcas Comerciais",
          "definition": "Ambientes multi-empresa habilitados."
        },
        {
          "id": "plan_embudos",
          "title": "Funis de Vendas",
          "definition": "Pipelines e quadros Kanban ativos."
        },
        {
          "id": "plan_conexiones_qr",
          "title": "Conexões WhatsApp QR",
          "definition": "Números conectados via QR Code."
        },
        {
          "id": "plan_conexiones_oficial",
          "title": "Conexões WhatsApp API Oficial",
          "definition": "Canais oficiais Meta Business API."
        },
        {
          "id": "plan_redes_sociales",
          "title": "Canais Sociais",
          "definition": "Integrações ativas de Instagram, Messenger e Telegram."
        },
        {
          "id": "plan_correos",
          "title": "Contas de E-mail",
          "definition": "Caixas postais sincronizadas no CRM."
        },
        {
          "id": "plan_flujos",
          "title": "Fluxos de Chatbot",
          "definition": "Fluxos inteligentes publicados no Flowbuilder."
        },
        {
          "id": "plan_publicaciones",
          "title": "Disparos em Massa",
          "definition": "Publicações em status, grupos e comunidades."
        },
        {
          "id": "plan_widgets",
          "title": "Widgets Web",
          "definition": "Chats instalados em sites externos."
        },
        {
          "id": "plan_mensajes_mes",
          "title": "Mensagens do Mês",
          "definition": "Consumo da franquia mensal de mensagens."
        }
      ]
    },
    {
      "id": "nps",
      "title": "4. Pesquisa de Satisfação (NPS)",
      "description": "Indicadores de qualidade de atendimento avaliados pelos clientes.",
      "items": [
        {
          "id": "nps_score",
          "title": "Score NPS Geral",
          "definition": "% Promotores menos % Detratores."
        },
        {
          "id": "nps_promotores",
          "title": "Promotores (9-10)",
          "definition": "Clientes plenamente satisfeitos e leais."
        },
        {
          "id": "nps_neutrales",
          "title": "Neutros (7-8)",
          "definition": "Clientes satisfeitos mas passivos."
        },
        {
          "id": "nps_detractores",
          "title": "Detratores (1-6)",
          "definition": "Clientes insatisfeitos com a experiência."
        }
      ]
    },
    {
      "id": "evaluaciones",
      "title": "5. Dados de Atendimentos e Avaliações",
      "description": "Métricas de fechamento de tickets e taxa de resposta.",
      "items": [
        {
          "id": "total_tickets",
          "title": "Total de Atendimentos",
          "definition": "Volume total de tickets no período."
        },
        {
          "id": "tickets_espera_eval",
          "title": "Aguardando Avaliação",
          "definition": "Tickets com pesquisa enviada aguardando resposta."
        },
        {
          "id": "tickets_sin_eval",
          "title": "Sem Avaliação",
          "definition": "Tickets encerrados sem nota do cliente."
        },
        {
          "id": "tickets_evaluados",
          "title": "Avaliados",
          "definition": "Tickets com pesquisa respondida."
        },
        {
          "id": "indice_eval",
          "title": "Índice de Avaliação",
          "definition": "Percentual de tickets avaliados sobre o total."
        }
      ]
    },
    {
      "id": "rendimiento_asesores",
      "title": "6. Desempenho por Atendente",
      "description": "Tabela detalhada de produtividade individual.",
      "items": [
        {
          "id": "asesor_nombre",
          "title": "Atendente",
          "definition": "Identificação do membro da equipe."
        },
        {
          "id": "asesor_score",
          "title": "Score Médio",
          "definition": "Nota média NPS do atendente."
        },
        {
          "id": "asesor_atendidos",
          "title": "Atendidos e Avaliados",
          "definition": "Quantidade de conversas finalizadas."
        },
        {
          "id": "asesor_tpe",
          "title": "TME (Tempo Médio de Espera)",
          "definition": "Média de espera antes do atendimento."
        },
        {
          "id": "asesor_tpa",
          "title": "TMA (Tempo Médio de Atendimento)",
          "definition": "Duração média dos atendimentos."
        },
        {
          "id": "asesor_estado",
          "title": "Status em Tempo Real",
          "definition": "Indicador online/offline."
        },
        {
          "id": "asesor_export",
          "title": "Exportar Excel",
          "definition": "Download dos dados em planilha."
        }
      ]
    },
    {
      "id": "graficos",
      "title": "7. Gráficos de Tendência e Horários",
      "description": "Distribuição de atendimentos por equipe e horários de pico.",
      "items": [
        {
          "id": "grafico_usuario",
          "title": "Atendimentos por Usuário",
          "definition": "Comparativo de carga de trabalho."
        },
        {
          "id": "grafico_tiempo",
          "title": "Fluxo por Horário",
          "definition": "Identificação de momentos de maior demanda."
        }
      ]
    },
    {
      "id": "faq",
      "title": "8. Perguntas Frequentes (FAQ)",
      "description": "Dúvidas e boas práticas de operação.",
      "items": [
        {
          "id": "faq_refresh",
          "title": "É necessário atualizar a página?",
          "definition": "Não, as informações chegam via WebSockets."
        },
        {
          "id": "faq_offline",
          "title": "Por que o atendente aparece Offline?",
          "definition": "Ocorre quando a aba do navegador é fechada."
        },
        {
          "id": "faq_optimizar",
          "title": "Como reduzir o tempo de espera?",
          "definition": "Distribua os atendentes de forma balanceada nas filas."
        }
      ]
    }
  ]
};

const fr = {
  "header": {
    "badge": "Guide Officiel BotMix",
    "title": "Centre de Gestion et Surveillance en Direct — Dashboard",
    "summary": "Le tableau de bord est le centre de commande de votre entreprise dans BotMix. Suivez en temps réel les échanges, la productivité des conseillers, la satisfaction client (NPS) et la consommation des ressources du forfait mensuel via WebSockets."
  },
  "sections": [
    {
      "id": "filtros",
      "title": "1. Filtres d'Affichage et Période",
      "description": "Outils de segmentation chronologique des indicateurs.",
      "items": [
        {
          "id": "toggle_filters",
          "title": "Afficher / Masquer les Filtres",
          "definition": "Affiche ou masque la barre de sélection des dates."
        },
        {
          "id": "date_range",
          "title": "Plage de Dates",
          "definition": "Définit les bornes temporelles d'analyse."
        },
        {
          "id": "btn_filter",
          "title": "Bouton Filtrer",
          "definition": "Recalcule instantanément l'ensemble des indicateurs."
        }
      ]
    },
    {
      "id": "indicadores",
      "title": "2. Indicateurs Généraux en Direct (KPIs)",
      "description": "Métriques reflétant l'activité instantanée du service client.",
      "items": [
        {
          "id": "atencion_actual",
          "title": "En Cours de Traitement",
          "definition": "Clients échangeant actuellement avec un conseiller."
        },
        {
          "id": "esperando",
          "title": "En Attente",
          "definition": "Clients en file d'attente attendant une prise en charge."
        },
        {
          "id": "finalizados",
          "title": "Finalisés",
          "definition": "Tickets résolus et clôturés sur la période."
        },
        {
          "id": "grupos",
          "title": "Groupes",
          "definition": "Groupes WhatsApp actifs connectés au système."
        },
        {
          "id": "agentes_activos",
          "title": "Agents Actifs",
          "definition": "Nombre de conseillers connectés en ligne."
        },
        {
          "id": "contactos_plan",
          "title": "Contacts du Forfait",
          "definition": "Contacts enregistrés par rapport au forfait."
        },
        {
          "id": "mensajes_recibidos",
          "title": "Messages Reçus",
          "definition": "Volume total de messages entrants."
        },
        {
          "id": "mensajes_enviados",
          "title": "Messages Envoyés",
          "definition": "Total des messages émis par agents et bots."
        },
        {
          "id": "tiempo_atencion",
          "title": "Durée Moyenne de Traitement (DMT)",
          "definition": "Temps moyen passé par ticket résolu."
        },
        {
          "id": "tiempo_espera",
          "title": "Temps Moyen d'Attente (TMA)",
          "definition": "Attente moyenne avant la prise en charge."
        },
        {
          "id": "iniciados_agente",
          "title": "Initié par l'Agent",
          "definition": "Tickets sortants créés manuellement avec '+'."
        },
        {
          "id": "iniciados_cliente",
          "title": "Initié par le Client",
          "definition": "Tickets entrants créés par le message d'un client."
        }
      ]
    },
    {
      "id": "consumo_plan",
      "title": "3. Consommation du Forfait",
      "description": "Suivi des quotas et ressources de votre abonnement.",
      "items": [
        {
          "id": "plan_contactos",
          "title": "Contacts Enregistrés",
          "definition": "Total des contacts stockés dans le CRM."
        },
        {
          "id": "plan_marcas",
          "title": "Marques Commerciales",
          "definition": "Espaces multi-marques activés."
        },
        {
          "id": "plan_embudos",
          "title": "Tunnels de Vente",
          "definition": "Pipelines et étapes Kanban actifs."
        },
        {
          "id": "plan_conexiones_qr",
          "title": "Connexions WhatsApp QR",
          "definition": "Comptes connectés par QR code."
        },
        {
          "id": "plan_conexiones_oficial",
          "title": "Connexions WhatsApp API Officielle",
          "definition": "Comptes certifiés Meta Business API."
        },
        {
          "id": "plan_redes_sociales",
          "title": "Réseaux Sociaux",
          "definition": "Canaux Instagram, Messenger et Telegram."
        },
        {
          "id": "plan_correos",
          "title": "Comptes E-mail",
          "definition": "Boîtes mail synchronisées dans le CRM."
        },
        {
          "id": "plan_flujos",
          "title": "Flux Automatisés",
          "definition": "Scénarios interactifs Flowbuilder."
        },
        {
          "id": "plan_publicaciones",
          "title": "Diffusions de Masse",
          "definition": "Publications en statuts, groupes et communautés."
        },
        {
          "id": "plan_widgets",
          "title": "Widgets Web",
          "definition": "Modules de chat installés sur des sites web."
        },
        {
          "id": "plan_mensajes_mes",
          "title": "Messages du Mois",
          "definition": "Consommation du quota mensuel de messages."
        }
      ]
    },
    {
      "id": "nps",
      "title": "4. Enquête de Satisfaction (NPS)",
      "description": "Mesure de la qualité du service perçue par les clients.",
      "items": [
        {
          "id": "nps_score",
          "title": "Score NPS Global",
          "definition": "% Promoteurs moins % Détracteurs."
        },
        {
          "id": "nps_promotores",
          "title": "Promoteurs (9-10)",
          "definition": "Clients enthousiastes et fidèles."
        },
        {
          "id": "nps_neutrales",
          "title": "Neutres (7-8)",
          "definition": "Clients satisfaits mais passifs."
        },
        {
          "id": "nps_detractores",
          "title": "Détracteurs (1-6)",
          "definition": "Clients insatisfaits du service."
        }
      ]
    },
    {
      "id": "evaluaciones",
      "title": "5. Bilan des Évaluations",
      "description": "Volume des tickets notés et taux de réponse.",
      "items": [
        {
          "id": "total_tickets",
          "title": "Total des Tickets",
          "definition": "Volume global de dossiers traités."
        },
        {
          "id": "tickets_espera_eval",
          "title": "En Attente de Note",
          "definition": "Enquête envoyée sans réponse."
        },
        {
          "id": "tickets_sin_eval",
          "title": "Sans Évaluation",
          "definition": "Tickets clôturés sans questionnaire."
        },
        {
          "id": "tickets_evaluados",
          "title": "Évalués",
          "definition": "Tickets ayant reçu une note."
        },
        {
          "id": "indice_eval",
          "title": "Taux d'Évaluation",
          "definition": "Pourcentage de tickets évalués."
        }
      ]
    },
    {
      "id": "rendimiento_asesores",
      "title": "6. Performance par Conseiller",
      "description": "Tableau récapitulatif individuel des agents.",
      "items": [
        {
          "id": "asesor_nombre",
          "title": "Conseiller",
          "definition": "Nom et identifiant de l'agent."
        },
        {
          "id": "asesor_score",
          "title": "Score Moyen",
          "definition": "Note NPS moyenne attribuée."
        },
        {
          "id": "asesor_atendidos",
          "title": "Tickets Traités",
          "definition": "Volume de conversations résolues."
        },
        {
          "id": "asesor_tpe",
          "title": "TMA (Attente Moyenne)",
          "definition": "Délai moyen avant prise en charge."
        },
        {
          "id": "asesor_tpa",
          "title": "DMT (Traitement Moyen)",
          "definition": "Durée moyenne de résolution."
        },
        {
          "id": "asesor_estado",
          "title": "Statut en Direct",
          "definition": "Indicateur en ligne / hors ligne."
        },
        {
          "id": "asesor_export",
          "title": "Export Excel",
          "definition": "Téléchargement des statistiques."
        }
      ]
    },
    {
      "id": "graficos",
      "title": "7. Graphiques de Tendance",
      "description": "Répartition de la charge et affluence horaire.",
      "items": [
        {
          "id": "grafico_usuario",
          "title": "Distribution par Conseiller",
          "definition": "Comparatif du volume d'activité."
        },
        {
          "id": "grafico_tiempo",
          "title": "Flux Temporel",
          "definition": "Identification des heures de pointe."
        }
      ]
    },
    {
      "id": "faq",
      "title": "8. Foire Aux Questions (FAQ)",
      "description": "Recommandations et bonnes pratiques d'exploitation.",
      "items": [
        {
          "id": "faq_refresh",
          "title": "Faut-il rafraîchir la page ?",
          "definition": "Non, les flux sont transmis par WebSockets."
        },
        {
          "id": "faq_offline",
          "title": "Pourquoi un conseiller apparaît hors ligne ?",
          "definition": "L'onglet a été fermé ou la connexion perdue."
        },
        {
          "id": "faq_optimizar",
          "title": "Comment réduire les délais d'attente ?",
          "definition": "Répartissez équitablement les files d'attente."
        }
      ]
    }
  ]
};

const ar = {
  "header": {
    "badge": "دليل BotMix الرسمي",
    "title": "مركز الإدارة والمراقبة المباشرة — لوحة التحكم",
    "summary": "لوحة التحكم هي مركز القيادة والتحكم لشركتك في BotMix. راقب في الوقت الفعلي تدفق المحادثات، وإنتاجية الوكلاء، ورضا العملاء (NPS)، واستهلاك موارد الباقة الشهرية عبر WebSockets دون الحاجة لتحديث الصفحة."
  },
  "sections": [
    {
      "id": "filtros",
      "title": "1. فلاتر العرض والنطاق الزمني",
      "description": "أدوات لتقسيم المقاييس والتقارير زمنيًا.",
      "items": [
        {
          "id": "toggle_filters",
          "title": "إظهار / إخفاء الفلاتر",
          "definition": "فتح أو إغلاق شريط اختيار التواريخ لعرض واضح ومريح."
        },
        {
          "id": "date_range",
          "title": "نطاق التواريخ",
          "definition": "تحديد الحدود الزمنية الدقيقة للتقرير."
        },
        {
          "id": "btn_filter",
          "title": "زر التصفية",
          "definition": "إعادة حساب العدادات والمؤشرات فورًا وفقًا للتواريخ المحددة."
        }
      ]
    },
    {
      "id": "indicadores",
      "title": "2. مؤشرات المحادثة المباشرة (KPIs)",
      "description": "مقاييس رقمية تعكس حالة الاتصالات في الوقت الفعلي.",
      "items": [
        {
          "id": "atencion_actual",
          "title": "الخدمة الحالية",
          "definition": "عدد العملاء الذين يجرون محادثة نشطة حاليًا مع وكيل بشري."
        },
        {
          "id": "esperando",
          "title": "في الانتظار (الطابور)",
          "definition": "عملاء ينتظرون في طابور الانتظار استلام تذاكرهم من قبل وكيل."
        },
        {
          "id": "finalizados",
          "title": "المكتملة",
          "definition": "إجمالي التذاكر المغلقة والمحلولة خلال الفترة."
        },
        {
          "id": "grupos",
          "title": "المجموعات",
          "definition": "مجموعات WhatsApp النشطة المتصلة بالنظام."
        },
        {
          "id": "agentes_activos",
          "title": "الوكلاء النشطون",
          "definition": "الوكلاء المتصلون حاليًا بالإنترنت مقارنة بإجمالي الوكلاء."
        },
        {
          "id": "contactos_plan",
          "title": "جهات اتصال الباقة",
          "definition": "الجهات المحفوظة مقارنة بالحد الأقصى للباقة."
        },
        {
          "id": "mensajes_recibidos",
          "title": "الرسائل المستلمة",
          "definition": "إجمالي الرسائل الواردة عبر جميع القنوات."
        },
        {
          "id": "mensajes_enviados",
          "title": "الرسائل المرسلة",
          "definition": "إجمالي الرسائل الصادرة من الوكلاء والبوتات."
        },
        {
          "id": "tiempo_atencion",
          "title": "متوسط وقت الخدمة (AST)",
          "definition": "الوقت المستغرق من استلام التذكرة حتى حلها."
        },
        {
          "id": "tiempo_espera",
          "title": "متوسط وقت الانتظار (AWT)",
          "definition": "متوسط انتظار العميل في الطابور قبل الاستلام."
        },
        {
          "id": "iniciados_agente",
          "title": "بدأها الوكيل",
          "definition": "تذاكر صادرة أنشأها الوكلاء يدويًا عبر زر '+'."
        },
        {
          "id": "iniciados_cliente",
          "title": "بدأها العميل",
          "definition": "تذاكر واردة ناتجة عن رسائل العملاء."
        }
      ]
    },
    {
      "id": "consumo_plan",
      "title": "3. استهلاك الباقة (الحدود الشهرية)",
      "description": "متابعة استهلاك موارد باقة الاشتراك النشطة.",
      "items": [
        {
          "id": "plan_contactos",
          "title": "جهات الاتصال المخزنة",
          "definition": "إجمالي جهات الاتصال في CRM."
        },
        {
          "id": "plan_marcas",
          "title": "العلامات التجارية",
          "definition": "مساحات العمل متعددة العلامات المتاحة."
        },
        {
          "id": "plan_embudos",
          "title": "مسارات البيع",
          "definition": "لوحات ومراحل Kanban النشطة."
        },
        {
          "id": "plan_conexiones_qr",
          "title": "قنوات WhatsApp QR",
          "definition": "أرقام WhatsApp المتصلة عبر رمز الاستجابة."
        },
        {
          "id": "plan_conexiones_oficial",
          "title": "قنوات WhatsApp الرسمية (Meta API)",
          "definition": "قنوات واجهة برمجة تطبيقات Meta الرسمية."
        },
        {
          "id": "plan_redes_sociales",
          "title": "قنوات التواصل الاجتماعي",
          "definition": "قنوات Instagram وMessenger وTelegram."
        },
        {
          "id": "plan_correos",
          "title": "حسابات البريد الإلكتروني",
          "definition": "صناديق البريد المتزامنة في المنظومة."
        },
        {
          "id": "plan_flujos",
          "title": "المسارات الآلية (Flows)",
          "definition": "سيناريوهات البوت المنشورة عبر Flowbuilder."
        },
        {
          "id": "plan_publicaciones",
          "title": "الإرسال الجماعي",
          "definition": "المنشورات الجماعية للحالات والمجموعات."
        },
        {
          "id": "plan_widgets",
          "title": "أدوات الويب (Widgets)",
          "definition": "نوافذ الدردشة المضمنة في المواقع."
        },
        {
          "id": "plan_mensajes_mes",
          "title": "رسائل الشهر",
          "definition": "استهلاك الحصة الشهرية للرسائل."
        }
      ]
    },
    {
      "id": "nps",
      "title": "4. استطلاع رضا العملاء (NPS)",
      "description": "مقاييس رضا وتقييم جودة الخدمة من العملاء.",
      "items": [
        {
          "id": "nps_score",
          "title": "معدل NPS العام",
          "definition": "نسبة المروجين مطروحًا منها نسبة المنتقدين."
        },
        {
          "id": "nps_promotores",
          "title": "المروجون (9-10)",
          "definition": "عملاء راضون ومخلصون يوصون بخدمتك."
        },
        {
          "id": "nps_neutrales",
          "title": "المحايدون (7-8)",
          "definition": "عملاء راضون ولكن دون ولاء قوي."
        },
        {
          "id": "nps_detractores",
          "title": "المنتقدون (1-6)",
          "definition": "عملاء غير راضين عن التجربة."
        }
      ]
    },
    {
      "id": "evaluaciones",
      "title": "5. معلومات التذاكر والتقييمات",
      "description": "إحصائيات إغلاق التذاكر ومعدلات الاستجابة.",
      "items": [
        {
          "id": "total_tickets",
          "title": "إجمالي التذاكر",
          "definition": "حجم التذاكر المعالجة بالكامل."
        },
        {
          "id": "tickets_espera_eval",
          "title": "بانتظار التقييم",
          "definition": "تذاكر أُرسل استبيانها وبانتظار الرد."
        },
        {
          "id": "tickets_sin_eval",
          "title": "بدون تقييم",
          "definition": "تذاكر أُغلقت دون تعبئة التقييم."
        },
        {
          "id": "tickets_evaluados",
          "title": "تم تقييمها",
          "definition": "تذاكر تم الرد على تقييمها."
        },
        {
          "id": "indice_eval",
          "title": "مؤشر التقييم",
          "definition": "نسبة التذاكر المقيّمة من الإجمالي."
        }
      ]
    },
    {
      "id": "rendimiento_asesores",
      "title": "6. أداء الوكلاء الفردي",
      "description": "جدول مفصل لإنتاجية كل وكيل.",
      "items": [
        {
          "id": "asesor_nombre",
          "title": "الوكيل",
          "definition": "اسم ومعرف العضو في الفريق."
        },
        {
          "id": "asesor_score",
          "title": "متوسط التقييم",
          "definition": "معدل تقييمات NPS التي نالها الوكيل."
        },
        {
          "id": "asesor_atendidos",
          "title": "المخدومة والمقيمة",
          "definition": "عدد المحادثات المنجزة والمقيمة."
        },
        {
          "id": "asesor_tpe",
          "title": "AWT (متوسط الانتظار)",
          "definition": "متوسط وقت انتظار العملاء قبل الرد."
        },
        {
          "id": "asesor_tpa",
          "title": "AST (متوسط الخدمة)",
          "definition": "متوسط مدة معالجة التذكرة."
        },
        {
          "id": "asesor_estado",
          "title": "الحالة المباشرة",
          "definition": "مؤشر متصل / غير متصل بالإنترنت."
        },
        {
          "id": "asesor_export",
          "title": "تصدير إلى Excel",
          "definition": "تنزيل تقرير الأداء في ملف إكسل."
        }
      ]
    },
    {
      "id": "graficos",
      "title": "7. الرسوم البيانية والتوزيع الساعي",
      "description": "توزيع أعباء العمل وساعات الذروة.",
      "items": [
        {
          "id": "grafico_usuario",
          "title": "التوزيع حسب الوكيل",
          "definition": "مقارنة حجم العمل المنجز."
        },
        {
          "id": "grafico_tiempo",
          "title": "التدفق الزمني",
          "definition": "تحديد ساعات الإقبال الأكبر."
        }
      ]
    },
    {
      "id": "faq",
      "title": "8. الأسئلة الشائعة (FAQ)",
      "description": "إرشادات وأفضل ممارسات الاستخدام.",
      "items": [
        {
          "id": "faq_refresh",
          "title": "هل يلزم تحديث الصفحة يدويًا؟",
          "definition": "لا، البيانات تتدفق عبر WebSockets تلقائيًا."
        },
        {
          "id": "faq_offline",
          "title": "لماذا يظهر الوكيل غير متصل؟",
          "definition": "يحدث عند إغلاق تبويب المتصفح أو انقطاع الاتصال."
        },
        {
          "id": "faq_optimizar",
          "title": "كيف تخفض أوقات الانتظار؟",
          "definition": "وزع الوكلاء بتوازن على الطوابير وفعل الترحيب الآلي."
        }
      ]
    }
  ]
};

export const DASHBOARD_HELP_DATA = {
  es,
  en,
  pt,
  fr,
  ar,
};
